/**
 * ActiveWorkBar Component
 *
 * Floating bar that appears when current user has an active timer.
 * Shows work order info, elapsed time, and quick actions.
 *
 * Features:
 * - Displays when timer is running
 * - Shows elapsed time with live updates
 * - Stop button to end timer
 * - Click to navigate to work order
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import { Square, Wrench, Clock, X, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useActiveTimerRealtime, useStopTimer } from '../../hooks/useActiveTimer';

/**
 * Format elapsed time from start timestamp
 */
function formatElapsedTime(startedAt: string): string {
  const start = new Date(startedAt).getTime();
  const now = Date.now();
  const elapsedMs = now - start;

  const hours = Math.floor(elapsedMs / (1000 * 60 * 60));
  const minutes = Math.floor((elapsedMs % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((elapsedMs % (1000 * 60)) / 1000);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}

export function ActiveWorkBar() {
  const { t } = useTranslation(['garage', 'common']);
  const navigate = useNavigate();
  const { data: activeTimer, isLoading } = useActiveTimerRealtime();
  const stopTimerMutation = useStopTimer();

  // Elapsed time state (updates every second)
  const [elapsedTime, setElapsedTime] = useState<string>('0:00');

  // Dismissed state (user can temporarily hide the bar)
  const [isDismissed, setIsDismissed] = useState(false);

  // Reset dismissed state when timer changes
  useEffect(() => {
    if (activeTimer?.workOrderId) {
      setIsDismissed(false);
    }
  }, [activeTimer?.workOrderId]);

  // Update elapsed time every second
  useEffect(() => {
    if (!activeTimer?.startedAt) {
      setElapsedTime('0:00');
      return;
    }

    const updateElapsed = () => {
      setElapsedTime(formatElapsedTime(activeTimer.startedAt));
    };

    updateElapsed();
    const interval = setInterval(updateElapsed, 1000);
    return () => clearInterval(interval);
  }, [activeTimer?.startedAt]);

  // Handle stop timer
  const handleStop = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await stopTimerMutation.mutateAsync();
    } catch {
      // Error handled by mutation
    }
  };

  // Handle click to navigate to work order
  const handleClick = () => {
    if (activeTimer?.workOrderId) {
      // Navigate to garage with dialog open for this work order
      navigate(`/garage?dialog=workOrder&mode=view&id=${activeTimer.workOrderId}`);
    }
  };

  // Handle dismiss
  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDismissed(true);
  };

  // Don't render if no active timer, loading, or dismissed
  if (isLoading || !activeTimer || isDismissed) {
    return null;
  }

  const content = (
    <div
      className={cn(
        'fixed bottom-4 left-1/2 -translate-x-1/2 z-50',
        'max-w-md w-[calc(100%-2rem)]',
        'animate-in slide-in-from-bottom-4 fade-in duration-300'
      )}
    >
      <div
        onClick={handleClick}
        className={cn(
          'flex items-center gap-3 px-4 py-3',
          'bg-primary text-primary-foreground',
          'rounded-xl shadow-lg cursor-pointer',
          'hover:bg-primary/90 transition-colors',
          'border border-primary-foreground/10'
        )}
      >
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="h-10 w-10 rounded-full bg-primary-foreground/20 flex items-center justify-center">
            <Wrench className="h-5 w-5" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium opacity-80">
              {t('garage:time.workingOn', 'Bezig met')}
            </span>
            <span className="h-2 w-2 rounded-full bg-red-400 animate-pulse" />
          </div>
          <p className="font-semibold truncate">
            {activeTimer.workOrderTitle || t('garage:time.workOrder', 'Werkorder')}
          </p>
          {activeTimer.vehiclePlate && (
            <p className="text-xs opacity-70">{activeTimer.vehiclePlate}</p>
          )}
        </div>

        {/* Timer */}
        <div className="flex items-center gap-2 flex-shrink-0">
          <Clock className="h-4 w-4 opacity-70" />
          <span className="font-mono text-lg font-bold tabular-nums">{elapsedTime}</span>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <Button
            onClick={handleStop}
            variant="secondary"
            size="sm"
            className="h-8 gap-1.5 bg-primary-foreground/20 hover:bg-primary-foreground/30 text-primary-foreground border-0"
            disabled={stopTimerMutation.isPending}
          >
            <Square className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{t('garage:time.stop', 'Stop')}</span>
          </Button>
          <Button
            onClick={handleDismiss}
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-primary-foreground/70 hover:text-primary-foreground hover:bg-primary-foreground/10"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">{t('common:dismiss', 'Sluiten')}</span>
          </Button>
        </div>

        {/* Arrow indicator */}
        <ChevronRight className="h-4 w-4 opacity-50 flex-shrink-0" />
      </div>
    </div>
  );

  // Render in portal to ensure it's above everything
  if (typeof document === 'undefined') return null;
  return createPortal(content, document.body);
}
